import * as React from 'react';

import {
    OnSearchProps,
    ResultCardItem,
    Typography,
    UniversalSearchResult,
} from '@comfama/comfama-ui-react';

import useElasticSearch, {
    ElasticSearchResult,
} from '../hooks/useElasticSearch';
import { searchConfig, getElasticOptions } from '../config/searchConfig';
import { saveSearchTerm } from '../utils/saveSearchTerm';
import {
    transformElasticResults,
    transformElasticSuggestion,
} from '../utils/transformElasticResults';
import { toCategoryQueryValue } from '../utils/toCategoryQueryValue';
import useMainFilter from '../hooks/useMainFilter';

export interface SearchResultsViewProps {
    /** Solo se consume `search`; en SSR y en tests puede no venir. */
    location?: { search?: string };
}

const SearchResultsView: React.FC<SearchResultsViewProps> = ({ location }) => {
    const mainFilter = useMainFilter();

    const [keywordParam, setKeywordParam] = React.useState<string>('');
    const [isFullSearch, setIsFullSearch] = React.useState(false);
    const [resultFull, setResultFull] = React.useState<ResultCardItem[]>([]);

    const { doSearch, state, goToPage } =
        useElasticSearch<ElasticSearchResult>(getElasticOptions());

    // Inicializa la búsqueda leyendo ?q= o ?search= del parámetro de la URL.
    React.useEffect(() => {
        const searchStr =
            location?.search ??
            (typeof window !== 'undefined' ? window.location.search : '');
        const params = new URLSearchParams(searchStr);
        const initialQuery = params.get('q') || params.get('search') || '';
        if (!initialQuery.trim()) return;

        setKeywordParam(initialQuery);
        setIsFullSearch(true);
        const boostIndex = searchConfig.elastic.boostIndex[0];
        doSearch(
            initialQuery,
            {},
            [
                {
                    _script: {
                        type: 'number',
                        script: {
                            source: `return doc['_index'].value == '${boostIndex}' ? 0 : 1;`,
                        },
                        order: 'asc' as const,
                    },
                },
            ],
            1
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Función para manejar la búsqueda en tiempo real (sugerencias)
    const handleSearch = (searchValue: string) => {
        if (searchValue.trim()) {
            doSearch(searchValue, {});
        }
    };

    const handleSearchFull = ({
        query,
        selectedFilters,
        setLoading,
        currentPage,
    }: OnSearchProps) => {
        if (query) {
            saveSearchTerm(query);
            setKeywordParam(query);
        }
        setIsFullSearch(true);
        setLoading(false);

        const sortFields: Array<Record<string, unknown>> = [];
        const filterClauses: Array<unknown> = [];

        if (selectedFilters) {
            Object.keys(selectedFilters).forEach((key) => {
                const values = selectedFilters[key];
                const mappedField = searchConfig.filterMapping[key];
                if (mappedField && values.length > 0) {
                    filterClauses.push({
                        terms: {
                            [mappedField]: values.map(toCategoryQueryValue),
                        },
                    });

                    sortFields.push({
                        [mappedField]: {
                            order: 'asc' as const,
                            unmapped_type: 'keyword',
                        },
                    });
                }
            });
        }

        // Priorizar el índice destacado (boost) configurado.
        const [boostIndex] = searchConfig.elastic.boostIndex;
        sortFields.push({
            _script: {
                type: 'number',
                script: {
                    source: `return doc['_index'].value == '${boostIndex}' ? 0 : 1;`,
                },
                order: 'asc',
            },
        });

        doSearch(
            query || '*',
            { filter: filterClauses },
            sortFields,
            currentPage
        );
    };

    const searchSuggestions = transformElasticSuggestion(state.result || []);

    // Solo reacciona a la llegada de nuevos resultados: `isFullSearch` se omite
    // a propósito para no transformar con datos previos al disparar la búsqueda.
    React.useEffect(() => {
        if (isFullSearch) {
            const transformedResults = transformElasticResults(
                state.result || [],
                {}
            );
            setResultFull(transformedResults);
            setIsFullSearch(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.result]);

    return (
        <>
            <div className="cfm:max-w-7xl cfm:mx-auto cfm:px-4 cfm:pt-8">
                <Typography
                    variant="h2"
                    as="h1"
                    className="cfm:text-typography"
                >
                    {keywordParam && 'Tus resultados para'}
                    {keywordParam && (
                        <Typography
                            variant="h2"
                            className="cfm:text-primary"
                            as="span"
                        >
                            {' '}
                            &quot;{keywordParam}&quot;
                        </Typography>
                    )}
                </Typography>
            </div>

            <UniversalSearchResult
                currentPage={state.currentPage}
                filters={{
                    // `UniversalSearchResult` agrega `mainFilter` a esta lista;
                    // no hay más campos de filtro.
                    fields: [],
                    selectedValues: {},
                    hiddenFields: [],
                }}
                goToPage={goToPage}
                mainFilter={mainFilter}
                onSearch={handleSearch}
                searchSuggestions={searchSuggestions}
                isLoadingSuggestions={state.isLoading}
                onSearchFull={handleSearchFull}
                results={resultFull}
                totalPages={state.totalPages}
                totalResults={state.totalResults}
                hiddenTitle={true}
            />
        </>
    );
};

export default SearchResultsView;
